import React, { useState } from 'react';
import CloseIcon from '../../assets/images/closeIcon.svg';
import DeleteIcon from '../../assets/images/DeleteIcon.svg';
import { useUpdateNoteMutation } from '../../api/note';
import { updateNote } from '../../features/notes/noteSlice';
import { useDispatch, useSelector } from 'react-redux';
import { addNoteIcon, removeNoteIcon } from '../../utils/readiumUtils';
import store, { RootState } from '../../store';
import { Modal } from 'antd';

interface DisplayNoteModalProps {
    onClose: () => void;
    noteId: number;
    initialNote: string;
    bookId: string;
    handleDelete: () => void;
}

const DisplayNoteModal: React.FC<DisplayNoteModalProps> = ({
    onClose,
    noteId,
    initialNote,
    bookId,
    handleDelete,
}) => {
    const [note, setNote] = useState(initialNote);
    const dispatch = useDispatch();
    const [updateSingleNote] = useUpdateNoteMutation();
    const allNotes = useSelector((state: RootState) => state.notes.notes);
    const handleUpdate = async () => {
        try {
            await updateSingleNote({
                id: noteId,
                bookId: bookId,
                text: note,
            }).unwrap();
            dispatch(updateNote({ id: noteId, text: note }));
            onClose();
            const updatedNotes = store.getState().notes?.notes;
            addNoteIcon(window, updatedNotes);
        } catch (error) {
            console.error('Error updating note:', error);
        }
    };
    const confirmDelete = () => {
        Modal.confirm({
            title: 'Are you sure you want to delete this note?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => handleDeleteClicked(),
        });
    };
    const handleDeleteClicked = () => {
        handleDelete();
        removeNoteIcon(window, noteId);
        const updatedNotes = store.getState().notes?.notes;
        addNoteIcon(window, updatedNotes);
        onClose();
    };
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg w-[30rem] p-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">
                        Edit Note
                    </h2>
                    <button
                        onClick={onClose}
                        data-testid="display-note-modal-close-button"
                    >
                        <img src={CloseIcon} alt="Close" />
                    </button>
                </div>

                {/* Note Content */}

                <textarea
                    className="w-full h-40 rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                ></textarea>

                <div className="flex items-center justify-between mt-4">
                    <div className="flex space-x-52 items-center">
                        <button
                            data-testid="display-note-modal-delete-button"
                            className="hover:bg-blue-100 rounded-full p-1 w-8 h-8"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                confirmDelete();
                            }}
                        >
                            <img src={DeleteIcon} alt="Delete" />
                        </button>
                        <div className="flex flex-row items-center space-x-2">
                            <button
                                data-testid="display-note-modal-cancel-button"
                                className="px-4 py-2 bg-gray-200 rounded-3xl hover:bg-gray-300"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="display-note-modal-update-button"
                                className="px-4 py-2 bg-blue-500 text-white rounded-3xl hover:bg-blue-600 flex items-center space-x-2"
                                onClick={handleUpdate}
                            >
                                <span>Update</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DisplayNoteModal;
